/**
 * ChildOKRSection
 *
 * Read-only list of the OKRs that roll up into this objective.
 *
 * Does no fetching: `okr.childOKRs` already arrives with the detail query, privacy-filtered
 * server-side, with each child's own progress computed there through the single shared
 * progress definition. There is therefore no loading state and no error state here.
 */
import React from 'react';
import { GitMerge } from 'lucide-react';
import { Badge } from '../common/Badge';
import { EmptyState } from '../common/EmptyState';
import { okrStatusLabel } from '../../utils/okrStatus';
import type { OKR } from '../../types/okr';

export type ChildOKR = NonNullable<OKR['childOKRs']>[number];

export interface ChildOKRSectionProps {
  /** Named childOKRs, not `children` — the latter would shadow React's own prop. */
  childOKRs: ChildOKR[] | undefined;
  onSelect: (id: string) => void;
}

const statusVariant = (status: string): 'success' | 'warning' | 'danger' | 'secondary' => {
  switch (status) {
    case 'on_track':
      return 'success';
    case 'at_risk':
      return 'warning';
    case 'off_track':
      return 'danger';
    default:
      return 'secondary';
  }
};

export const ChildOKRSection: React.FC<ChildOKRSectionProps> = ({ childOKRs, onSelect }) => {
  if (!childOKRs || childOKRs.length === 0) {
    return (
      <EmptyState
        icon={GitMerge}
        title="No OKRs currently roll up into this objective."
        description="Objectives aligned to this one will appear here."
      />
    );
  }

  // Deterministic order. The API applies no `orderBy` to childOKRs, so without this the
  // row order is at Prisma's discretion and can shift between requests. Least-progressed
  // first is the useful reading.
  const ordered = [...childOKRs].sort(
    (a, b) => a.progress - b.progress || a.title.localeCompare(b.title)
  );

  return (
    <div>
      <h2
        className="text-xl font-semibold text-stone-900 mb-4"
        style={{ fontFamily: 'Fraunces, Georgia, serif' }}
      >
        {ordered.length === 1
          ? '1 OKR rolls up into this objective'
          : `${ordered.length} OKRs roll up into this objective`}
      </h2>

      <ul className="divide-y divide-stone-200">
        {ordered.map((c) => (
          <li key={c.id} className="py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <button
                  type="button"
                  data-testid="child-okr-title"
                  onClick={() => onSelect(c.id)}
                  className="text-left font-medium text-stone-900 hover:text-primary-600 hover:underline"
                >
                  {c.title}
                </button>
                <div className="mt-1 flex items-center gap-2 text-sm text-stone-500">
                  <Badge variant="secondary" size="sm">
                    {c.type}
                  </Badge>
                  <Badge variant={statusVariant(c.status)} size="sm">
                    {okrStatusLabel(c.status)}
                  </Badge>
                  <span>
                    {c.owner.firstName} {c.owner.lastName}
                  </span>
                </div>
              </div>

              <div className="w-40 shrink-0">
                <div className="flex items-center justify-end gap-2">
                  <div
                    className="h-2 w-24 rounded-full bg-stone-200"
                    role="progressbar"
                    aria-valuenow={c.progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${c.title} progress`}
                  >
                    <div
                      className="h-2 rounded-full bg-primary-500"
                      style={{ width: `${Math.min(Math.max(c.progress, 0), 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-stone-700">{c.progress}%</span>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
